import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { PrFinancialSummary } from '@/types/domain/pr-management';
import { formatCurrency } from '@/lib/utils';
import { DollarSign } from 'lucide-react';

interface PrFinancialSummaryCardProps {
  financials: PrFinancialSummary;
}

export const PrFinancialSummaryCard: React.FC<PrFinancialSummaryCardProps> = ({ financials }) => {
  return (
    <Card className="border-border rounded-2xl shadow-2xs font-heading">
      <CardHeader className="pb-3 border-b border-border/60 flex items-center justify-between">
        <CardTitle className="font-heading text-xs font-bold text-foreground uppercase flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-primary" /> Financial Summary & Budget Availability
        </CardTitle>
        <span
          className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${
            financials.budgetStatus === 'SUFFICIENT'
              ? 'bg-emerald-500/10 text-emerald-700'
              : 'bg-amber-500/10 text-amber-700'
          }`}
        >
          {financials.budgetStatus} BUDGET
        </span>
      </CardHeader>

      <CardContent className="pt-4 space-y-4 text-xs">
        {/* Main Requisition Totals */}
        <div className="space-y-2 pb-3 border-b border-border/60">
          <div className="flex justify-between font-semibold text-foreground-secondary">
            <span>Line Items Subtotal</span>
            <span>{formatCurrency(financials.subtotal, financials.currency)}</span>
          </div>
          <div className="flex justify-between font-semibold text-foreground-secondary">
            <span>Estimated GST / Taxes (18%)</span>
            <span>{formatCurrency(financials.taxAmount, financials.currency)}</span>
          </div>
          <div className="flex justify-between font-extrabold text-foreground text-sm pt-1 border-t border-border/40">
            <span className="text-primary">Total Requisition Amount</span>
            <span className="text-primary font-mono">{formatCurrency(financials.totalAmount, financials.currency)}</span>
          </div>
        </div>

        {/* Department Budget Breakdown */}
        <div className="grid grid-cols-3 gap-2 text-center pt-1">
          <div className="p-2.5 rounded-xl border border-border bg-muted/30">
            <span className="text-[10px] font-bold text-foreground-muted uppercase block">Cost Center Budget</span>
            <span className="font-extrabold text-foreground">{formatCurrency(financials.budgetLimit, financials.currency)}</span>
          </div>
          <div className="p-2.5 rounded-xl border border-border bg-muted/30">
            <span className="text-[10px] font-bold text-foreground-muted uppercase block">Utilized Budget</span>
            <span className="font-extrabold text-foreground">{formatCurrency(financials.budgetConsumed, financials.currency)}</span>
          </div>
          <div className="p-2.5 rounded-xl border border-primary/20 bg-primary/5">
            <span className="text-[10px] font-bold text-primary uppercase block">Available Remaining</span>
            <span className="font-extrabold text-emerald-600 dark:text-emerald-400">
              {formatCurrency(financials.availableBudget, financials.currency)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
